import { useEffect, useRef } from 'react'
import { useAuth } from '../../../contexts/auth-context'
import { consentCallEndedCopy } from '@shared/calls/recording-consent'
import {
  setConsentAcceptedAck,
  useConsentAcceptedAck,
  useConsentCallEndedNotice,
} from '../../../stores/consent-call-ended-store'
import {
  dismissConsentCallEndedNotice,
  refreshConsentCallEndedNotice,
} from '../../../services/consent-call-ended-service'
import { setPendingRecordingPolicyGate } from '../../../stores/recording-policy-gate-store'

/**
 * "Call ended" popup after the server's 30-second recording-consent window
 * disconnected a call. Both parties see it (first names only). The user who
 * hadn't consented goes straight on to the existing recording-consent prompt.
 * Missed live (backgrounded / killed / in the car)? The account keeps the
 * notice, and it is fetched on launch and on every return to the foreground.
 */
export function ConsentCallEndedDialog() {
  const { user } = useAuth()
  const notice = useConsentCallEndedNotice()
  const ack = useConsentAcceptedAck()
  const primaryRef = useRef<HTMLButtonElement>(null)
  const userId = user?.userId ?? null

  useEffect(() => {
    if (!userId) return
    void refreshConsentCallEndedNotice()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshConsentCallEndedNotice()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [userId])

  const open = Boolean(userId && (notice || ack))

  useEffect(() => {
    if (open) primaryRef.current?.focus()
  }, [open, notice, ack])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (notice) void dismissConsentCallEndedNotice()
      else setConsentAcceptedAck(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, notice])

  if (!open) return null

  if (!notice) {
    return (
      <div
        className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-ack-title"
        aria-describedby="consent-ack-body"
      >
        <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl sm:p-6">
          <h2 id="consent-ack-title" className="text-base font-semibold text-slate-900">
            Thanks — you&apos;re all set
          </h2>
          <p id="consent-ack-body" className="mt-3 text-sm leading-relaxed text-slate-700" role="status">
            You&apos;ve agreed to call recording. You can make and receive calls again.
          </p>
          <div className="mt-5 flex justify-end">
            <button
              ref={primaryRef}
              type="button"
              onClick={() => setConsentAcceptedAck(false)}
              className="a11y-touch-target rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    )
  }

  const copy = consentCallEndedCopy(notice, userId)

  const reviewConsent = async () => {
    await dismissConsentCallEndedNotice()
    setPendingRecordingPolicyGate({
      mediaType: 'voice',
      proceed: () => setConsentAcceptedAck(true),
    })
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="consent-call-ended-title"
      aria-describedby="consent-call-ended-body"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl sm:p-6">
        <h2 id="consent-call-ended-title" className="text-base font-semibold text-slate-900">
          {copy.title}
        </h2>
        <p id="consent-call-ended-body" className="mt-3 text-sm leading-relaxed text-slate-700">
          {copy.body}
        </p>
        <div className="mt-5 flex flex-col gap-2">
          {copy.showConsentPrompt ? (
            <button
              ref={primaryRef}
              type="button"
              onClick={() => void reviewConsent()}
              className="a11y-touch-target w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
            >
              Continue to call recording consent
            </button>
          ) : (
            <button
              ref={primaryRef}
              type="button"
              onClick={() => void dismissConsentCallEndedNotice()}
              className="a11y-touch-target w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
